import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSuperAdminPortalView } from '../utils/hrAccess';
import { APP_NAME } from '../constants';

/**
 * The Admin area's own menu.
 *
 * The Admin page was a grid of cards, which worked for finding a screen once
 * and badly for the thing people actually do: change a shift, then a holiday,
 * then somebody's access. Each of those meant going back to the hub first,
 * because the main menu no longer lists the admin screens — they moved here.
 *
 * So the admin screens get their own sidebar, and it replaces the main one
 * while you are in them. Two menus side by side would be two places to look
 * for the same link, and the whole reason this area exists is that there is
 * one place.
 *
 * Nothing about the pages themselves changes. This is navigation only — every
 * route, guard and screen is the one that already existed and is already
 * tested.
 */

/**
 * Every route that belongs to the Admin area, including the ones reached from
 * inside a screen rather than from this menu.
 *
 * App.js decides which sidebar to render from this list, so a route left out
 * would show the main menu — and the main menu has no way back to admin, so
 * the person would be stranded on a page with no route home. The nested ones
 * (a QR code for a location, a workflow editor opened from settings) are the
 * easy ones to forget.
 */
export const ADMIN_PATHS = [
  '/admin',
  '/attendance-admin',
  '/attendance-month',
  '/attendance-qr',
  '/leave-admin',
  '/users',
  '/settings',
  '/workflows',
];

export const isAdminArea = (pathname) => ADMIN_PATHS.some(
  (p) => pathname === p || pathname.startsWith(`${p}/`)
);

const SECTIONS = [
  {
    label: 'Attendance',
    items: [
      { to: '/attendance-admin', icon: 'ti-map-pin-cog', label: 'Attendance Setup', perm: 'leave_admin' },
      { to: '/attendance-month', icon: 'ti-calendar-stats', label: 'Month End', perm: 'leave_admin' },
    ],
  },
  {
    label: 'Leave',
    items: [
      { to: '/leave-admin', icon: 'ti-calendar-cog', label: 'Leave Administration', perm: 'leave_admin' },
    ],
  },
  {
    /*
     * Full access only. Both of these can change who can reach what, so
     * Partial access does not list them and the routes refuse them — the menu
     * and the router agree, which is how a link stops being a dead end.
     */
    label: 'Platform',
    items: [
      { to: '/users', icon: 'ti-shield-lock', label: 'Users & Access', full: true },
      { to: '/settings', icon: 'ti-settings', label: 'Organisation Settings', full: true },
    ],
  },
];

const AdminSidebar = ({ open = false, onClose = () => {} }) => {
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const isAdmin = user?.role === 'admin';
  const isSuper = isSuperAdminPortalView(user);
  const navClass = ({ isActive }) => `nav-item${isActive ? ' active' : ''}`;

  const visible = useMemo(() => SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => {
      if (i.full) return isAdmin || isSuper || can('admin_full');
      if (i.perm) return can(i.perm);
      return true;
    }),
  })).filter((s) => s.items.length), [isAdmin, isSuper, can]);

  return (
    <aside className={`sidebar sidebar-admin${open ? ' open' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-icon" aria-hidden="true">
          <img src="/brand/tychi-mark.png" alt="" className="brand-icon-img" />
        </div>
        <div className="brand-text">
          <div className="brand-name">Admin</div>
          <div className="brand-sub">{APP_NAME}</div>
        </div>
        <button type="button" className="sidebar-close-btn" onClick={onClose} title="Close menu">
          <i className="ti ti-x"></i>
        </button>
      </div>

      {/*
        The way out, at the top where a back control belongs.
        Without it this area is a one-way door: the main menu is gone, and
        every admin screen leads only to other admin screens.
      */}
      <button type="button" className="adm-back" onClick={() => navigate('/dashboard')}>
        <i className="ti ti-arrow-left" /> Back to {APP_NAME}
      </button>

      <nav className="sidebar-nav">
        <NavLink to="/admin" end className={navClass}>
          <i className="ti ti-layout-grid nav-icon" /> Overview
        </NavLink>

        {visible.map((section) => (
          <React.Fragment key={section.label}>
            <div className="nav-section-label">{section.label}</div>
            {section.items.map((i) => (
              <NavLink key={i.to} to={i.to} className={navClass}>
                <i className={`ti ${i.icon} nav-icon`} /> {i.label}
              </NavLink>
            ))}
          </React.Fragment>
        ))}
      </nav>
    </aside>
  );
};

export default AdminSidebar;
